"use client";

import { useEffect, useRef, useState } from "react";
import gsap from "gsap";
import ScrollTrigger from "gsap/ScrollTrigger";
import ScrollSyncedVideoStage from "./ScrollSyncedVideoStage";
import * as THREE from "three";
import { OrbitControls } from "three/addons/controls/OrbitControls.js";
import { GLTFLoader } from "three/addons/loaders/GLTFLoader.js";
import { useTypedTexture } from "./useTypedTexture";
import { cn } from "../lib/utils";

//three.js setup
const scene = new THREE.Scene();
scene.background = new THREE.Color(0xaec972);
gsap.config({
  force3D: false
})
gsap.registerPlugin(ScrollTrigger);

/**
   *
   * Stage1:
   *  Hola Fátima, ya pasó más de un año desde que nos conocimos
  La verdad es que desde el momento en que hablé contigo, sentí que tú eras la persona con la que quería compartir el resto de mi vida, y rápidamente expresé las ganas que tenía de estar contigo para siempre
  <Fondo con manos y flores yendo hacia arriba> <lantanas> => 

  Stage2:
  Ha sido un año muy complicado para ti y para mí. Muchísimo esfuerzo y trabajo
  Gracias a Dios aún tenemos muchas experiencias por delante. Este es solo el comienzo de la mejor parte de nuestra vida. 
  <foto de IA de nosotros dos>

  Stage3:
  Por supuesto que no será fácil resolver todos los problemas que tendremos por delante, pero saldremos adelante, 100% seguro. Dios quiere que así sea.
  <Gif abrazo blanco y negro chica y chico dibujo>

  Stage4:
  Este año, aunque ha sido duro, ha sido infinitamente más fácil gracias a ti. Despertar y ver tu mensaje de buenos días me hace feliz. 
  <meme chico chica escribiéndose y dejando el móvil para dormir>

  Stage5:
  Escuchar tus anécdotas con tus hermanos, tus historias de Ghoulas, tus lecciones de japonés, tu preciosa voz cantando y diciéndome te quiero, el amor que demuestras cada día, no tiene precio. Me hace feliz.
  <crear gif con su vídeo de primer curso de japonés, zuzume canción y una foto ghibli o de su hermana>

  Stage6:
  Eres todo lo que necesitaba, he luchado y lucharé por estar a tu lado hasta la muerte. 


  Te amo,

  Jesús
  <Gif de muchos corazones random>

  StageFinal:
  <gif sésamo Elmo heart>
 */
/**
 * ScrollStages
 * ──────────────────────────────────────────────────────────────
 */
// ───── Contenido de cada fase ───────────────────────────
export const stageTexts = {
  stage1: `Hello Fátima,\n
It’s been more than a year since we met.\n
From the very first moment I felt you were the person I wanted to share my life with, and it didn't take me long to tell you I wanted to be with you forever...

`,

  stage2: `It has been a very complicated year for both of us. So much effort and work.\n
Thank God we still have many experiences ahead of us. This is only the beginning of the best part of our life.


`,

  stage3: `Of course it won't be easy to solve all the problems that lie ahead.\n
But we will get through them, I'm 100 % sure. God wants it that way.


`,

  stage4: `This year, even though it's been tough, has been infinitely easier thanks to you.\n
Waking up and reading your "good morning" message brings a smile to my face even before I fully open my eyes.


`,

  stage5: `Listening to your anecdotes with your brothers, your Ghoulas stories, your Japanese lessons,\n
Your lovely voice singing and telling me "I love you"

The love you show every day is priceless and makes me immensely happy.


`,

  stageFinal: `You are everything I ever needed.\n
I have fought and I will keep fighting forever to always be by your side.
Nmout 3lik ❤️\n
With all my heart,\n
        Jesús ❤️
        
        
        `,
} as const;

// arriba, antes del componente o dentro de él
const MOBILE_BP = 480; //  ≃  393 px que ves en Chrome DevTools

function getScaleForViewport() {
  return window.innerWidth < MOBILE_BP ? 0.55 : 1; // 0.55 ≈ ajusta a 393 px
}

const ScrollStages: React.FC = () => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [timeline, setTimeline] = useState<gsap.core.Timeline | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [paperMesh, setPaperMesh] = useState<THREE.Mesh | null>(null);

  const [typedText, setTypedText] = useState<string>(stageTexts.stage1);
  const letterGroupRef = useRef<THREE.Group | null>(null);
  const masterRef = useRef<gsap.core.Timeline | null>(null);
  const [textProgress, setTextProgress] = useState(0); // 0-1

  const stage1Texture = useTypedTexture({
    text: typedText,
    progress: textProgress, // 👈
  });

  const [progress, setProgress] = useState(0); // 0-100
  const [loading, setLoading] = useState(true);
  const [showHint, setShowHint] = useState({
    active: false,
    rotated: false,
  });
  const [started, setStarted] = useState(false);

  // 👆  estados / refs nuevos, ponlos junto a los otros:
  const audioRef = useRef<HTMLAudioElement | null>(null);

  const handleStart = () => {
    if (audioRef.current) {
      // 👉  prime de TODOS los vídeos que tengas en la página
      document.querySelectorAll(".special-video").forEach((v) => {
        const vid = v as HTMLVideoElement;
        vid
          .play()
          .then(() => vid.pause())
          .catch(() => {
            /* ignorar si ya está en autoplay-policy OK */
          });
      });

      audioRef.current
        .play()
        .then(() => {
          // audioRef.current!.pause();
          audioRef.current!.currentTime = 0;
        })
        .catch(() => {});
    }
    setStarted(true);
    setShowHint({ active: true, rotated: false });
    masterRef.current?.scrollTrigger?.enable();
    ScrollTrigger.refresh();
  };

  // ──────────────────────────────────────────────────────────
  useEffect(() => {
    // creamos el objeto Audio una sola vez
    audioRef.current = new Audio("/media/stage-5-song.mp3");
    audioRef.current.preload = "auto"; // descarga en segundo plano
    audioRef.current.volume = 0.4; // opcional
    // audioRef.current.src = "/media/wasureji-no-kotonoha.mp3";
    audioRef.current.src = "/media/modified-kotonoha.m4a";
  }, []);

  useEffect(() => {
    if (!canvasRef.current || !wrapperRef.current) return; // Safety guard – should never fire.
    // ----------------------------
    // 🎮  Three.js scene setup
    // ----------------------------
    const scene = new THREE.Scene();

    // Full-screen renderer bound to our <canvas>
    const renderer = new THREE.WebGLRenderer({
      canvas: canvasRef.current,
      antialias: true,
      alpha: true,
    });
    // renderer.setClearColor(0x000000, 0); // the default

    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setSize(window.innerWidth, window.innerHeight);
    renderer.shadowMap.enabled = true;
    renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1.7;

    // Camera + OrbitControls (mostly untouched from your original)
    const aspect = window.innerWidth / window.innerHeight;
    // const camera = new THREE.OrthographicCamera(-aspect * 50, aspect * 50, 50, -50, 1, 90);
    const fov = 45;
    // const aspect = 2;  // the canvas default
    const near = 1;
    const far = 100;
    const camera = new THREE.PerspectiveCamera(fov, aspect, near, far);

    camera.position.set(0.1, 14, 0.005);
    // camera.rotateY(Math.PI);
    // camera.rotateX(Math.PI);
    camera.rotateZ(Math.PI);
    // camera.position.set(0, 0,0);
    camera.zoom = 1;
    camera.updateProjectionMatrix();
    const controls = new OrbitControls(camera, renderer.domElement);
    controls.update();

    const manager = new THREE.LoadingManager();
    /* porcentaje durante la precarga de texturas / modelos */
    manager.onProgress = (_url, itemsLoaded, itemsTotal) => {
      setProgress(Math.round((itemsLoaded / itemsTotal) * 100));
    };
    manager.onLoad = () => {
      setLoading(false); // fade-out loader
      //   el “scrollHint” lo activaremos al acabar la intro (ver abajo)
    };
    const loader = new GLTFLoader(manager);
    loader.load(
      "/media/custom-letter2.glb",
      (glb) => {
        glb.scene.traverse((child) => {
          // Original traversal logic preserved
          if ((child as THREE.Mesh).isMesh) {
            const mesh = child as THREE.Mesh;
            mesh.castShadow = true;
            mesh.receiveShadow = true;
            console.log(child, "c");
          }
        });
        scene.add(glb.scene);
        const paper = glb.scene.getObjectByName("Object_8") as THREE.Mesh;
        if (paper) {
          const group = new THREE.Group();
          const LETTER_PARTS = [
            "Object_8",
            "Object_21",
            "Object_20",
            "heart",
            "Object_23",
            "Object_24",
            "Object_20001",
          ];

          LETTER_PARTS.forEach((name) => {
            const part = glb.scene.getObjectByName(name);
            if (part) group.add(part);
            const initialScale = getScaleForViewport();
            group.scale.setScalar(initialScale);
          });
          glb.scene.add(group);
          letterGroupRef.current = group;
          setPaperMesh(paper);

          const introTL = gsap.timeline({
            defaults: { duration: 3, ease: "power3.out" },
          });
          introTL
            .fromTo(group.position, { z: 11, x: 1, y: -0 }, { z: -0.12, x: 1.3, y: -0 })
            .fromTo(group.rotation, { x: 1, duration: 0.1 }, { x: 0 }, "<");
          /* ▸  Al terminar la intro… habilitamos el scrollTL */
          introTL.add(() => {
            masterRef?.current?.scrollTrigger?.enable(); // 🔓 ¡ya depende del scroll!
            ScrollTrigger.refresh(); // por si cambió el layout
            setShowHint({ active: true, rotated: false });
          });

          const move = 20;
          masterRef?.current?.fromTo(
            group.position,
            { z: -0.12, x: 1.3, y: -0 }, // entra por la derecha
            {
              z: -move,
              x: 6,
              y: -6,
            }, // sale por la izquierda
            "letterSpiralIn" // ⬅️ empieza a la vez que Stage 1
          );
          masterRef?.current?.fromTo(
            group.rotation,
            { x: 0, duration: 0.1 }, // entra por la derecha
            { x: 1.5 }, // sale por la izquierda
            "letterSpiralInRotation" // ⬅️ empieza a la vez que Stage 1
          );

          masterRef?.current?.fromTo(
            group.position,
            { z: -20, x: 1.3, y: -0 }, // entra por la derecha
            {
              z: 0,
              x: 1.3,
              y: 0,
            }, // sale por la izquierda
            "letterSpiralIn2" // ⬅️ empieza a la vez que Stage 1
          );
          masterRef?.current?.fromTo(
            group.rotation,
            { x: -2, duration: 0.1 }, // entra por la derecha
            { x: 0 }, // sale por la izquierda
            "letterSpiralInRotation2" // ⬅️ empieza a la vez que Stage 1
          );

          masterRef?.current?.fromTo(
            group.position,
            {
              z: 0,
              x: 1.3,
              y: 0,
            }, // entra por la derecha
            {
              z: 20,
              x: 1.3,
              y: 0,
            }, // sale por la izquierda
            "letterSpiralOut2" // ⬅️ empieza a la vez que Stage 1
          );
          masterRef?.current?.fromTo(
            group.rotation,
            { x: 0, duration: 0.1 }, // entra por la derecha
            { x: 2 }, // sale por la izquierda
            "letterSpiralOutRotation2" // ⬅️ empieza a la vez que Stage 1
          );

          masterRef?.current?.fromTo(
            group.position,
            { z: 20, x: 1.3, y: 0 }, // entra por la derecha
            {
              z: 0,
              x: 1.3,
              y: 0,
            }, // sale por la izquierda
            "letterSpiralIn3" // ⬅️ empieza a la vez que Stage 1
          );

          masterRef?.current?.fromTo(
            group.rotation,
            { x: 2, duration: 0.1 }, // entra por la derecha
            { x: 0 }, // sale por la izquierda
            "letterSpiralInRotation3" // ⬅️ empieza a la vez que Stage 1
          );

          masterRef?.current?.fromTo(
            group.position,
            {
              z: 0,
              x: 1.3,
              y: 0,
            }, // entra por la derecha
            {
              z: -20,
              x: 1.3,
              y: 0,
            }, // sale por la izquierda
            "letterSpiralOut3" // ⬅️ empieza a la vez que Stage 1
          );
          //  .fromTo(group.rotation, { x: 1, duration: 0.1 }, { x: 0 }, "<");
          masterRef?.current?.fromTo(
            group.rotation,
            { x: 0, duration: 0.1 }, // entra por la derecha
            { x: 2 }, // sale por la izquierda
            "letterSpiralOutRotation3" // ⬅️ empieza a la vez que Stage 1
          );

          masterRef?.current?.fromTo(
            group.position,
            { z: -20, x: 1.3, y: -0 }, // entra por la derecha
            {
              z: 0,
              x: 1.3,
              y: 0,
            }, // sale por la izquierda
            "letterSpiralIn4" // ⬅️ empieza a la vez que Stage 1
          );
          masterRef?.current?.fromTo(
            group.rotation,
            { x: -2, duration: 0.1 }, // entra por la derecha
            { x: 0 }, // sale por la izquierda
            "letterSpiralInRotation4" // ⬅️ empieza a la vez que Stage 1
          );

          masterRef?.current?.fromTo(
            group.position,
            {
              z: 0,
              x: 1.3,
              y: 0,
            }, // entra por la derecha
            {
              z: 20,
              x: 1.3,
              y: 0,
            }, // sale por la izquierda
            "letterSpiralOut4" // ⬅️ empieza a la vez que Stage 1
          );
          masterRef?.current?.fromTo(
            group.rotation,
            { x: 0, duration: 0.1 }, // entra por la derecha
            { x: 2 }, // sale por la izquierda
            "letterSpiralOutRotation4" // ⬅️ empieza a la vez que Stage 1
          );

          masterRef?.current
            ?.fromTo(
              group.position,
              { z: 20, x: 1.3, y: 0 }, // entra por la derecha
              {
                z: 0,
                x: 1.3,
                y: 0,
              }, // sale por la izquierda
              "letterSpiralIn5" // ⬅️ empieza a la vez que Stage 1
            )
            .add(() => {
              if (audioRef.current && !audioRef.current.src.includes("/media/stage-5-song.mp3")) {
                audioRef.current.volume = 1; // opcional
                audioRef.current.src = "/media/stage-5-song.mp3";
                audioRef.current.play();
                console.log(audioRef.current);
              }
            }, "<");

          masterRef?.current?.fromTo(
            group.rotation,
            { x: 2, duration: 0.1 }, // entra por la derecha
            { x: 0 }, // sale por la izquierda
            "letterSpiralInRotation5" // ⬅️ empieza a la vez que Stage 1
          );

          masterRef?.current?.fromTo(
            group.position,
            {
              z: 0,
              x: 1.3,
              y: 0,
            }, // entra por la derecha
            {
              z: -20,
              x: 1.3,
              y: 0,
            }, // sale por la izquierda
            "letterSpiralOut5" // ⬅️ empieza a la vez que Stage 1
          );
          //  .fromTo(group.rotation, { x: 1, duration: 0.1 }, { x: 0 }, "<");
          masterRef?.current?.fromTo(
            group.rotation,
            { x: 0, duration: 0.1 }, // entra por la derecha
            { x: 2 }, // sale por la izquierda
            "letterSpiralOutRotation5" // ⬅️ empieza a la vez que Stage 1
          );

          masterRef?.current?.fromTo(
            group.position,
            { z: -20, x: 1.3, y: -0 }, // entra por la derecha
            {
              z: 0,
              x: 1.3,
              y: 0,
            }, // sale por la izquierda
            "letterSpiralInFinal" // ⬅️ empieza a la vez que Stage 1
          );
          masterRef?.current?.fromTo(
            group.rotation,
            { x: -2, duration: 0.1 }, // entra por la derecha
            { x: 0 }, // sale por la izquierda
            "letterSpiralInRotationFinal" // ⬅️ empieza a la vez que Stage 1
          );

          masterRef?.current?.fromTo(
            group.position,
            {
              z: 0,
              x: 1.3,
              y: 0,
            }, // entra por la derecha
            {
              z: 20,
              x: 1.3,
              y: 0,
            }, // sale por la izquierda
            "letterSpiralOutFinal" // ⬅️ empieza a la vez que Stage 1
          );
          masterRef?.current?.fromTo(
            group.rotation,
            { x: 0, duration: 0.1 }, // entra por la derecha
            { x: 2 }, // sale por la izquierda
            "letterSpiralOutRotationFinal" // ⬅️ empieza a la vez que Stage 1
          );
        }
      },
      undefined,
      (err) => console.error(err)
    );

    // ---------------------------------------------------
    // Lighting – same as before
    // ---------------------------------------------------
    const sun = new THREE.DirectionalLight(0xffffff);
    sun.castShadow = true;
    sun.position.set(0, 2.5, 0);
    sun.target.position.set(0, 0, 6);
    sun.shadow.mapSize.set(4096, 4096);
    sun.shadow.camera.left = 150;
    sun.shadow.camera.right = 300;
    sun.shadow.camera.top = 150;
    sun.shadow.camera.bottom = -100;
    sun.shadow.normalBias = 0.2;
    sun.intensity = 9;

    scene.add(sun.target);
    scene.add(sun);

    const ambient = new THREE.AmbientLight(0x404040, 2.7);
    scene.add(ambient);

    //---------------------------------------------------
    //  Main animation loop  (Three.js → requestAnimationFrame)
    //---------------------------------------------------

    const animate = (): void => {
      renderer.render(scene, camera);
      requestAnimationFrame(animate);
    };

    animate();

    const onResize = (): void => {
      const { innerWidth: w, innerHeight: h } = window;
      if (letterGroupRef.current) {
        const s = getScaleForViewport();
        letterGroupRef.current.scale.setScalar(s);
      }

      // ②  si prefieres, corrige un poco la cámara
      camera.aspect = w / h;

      renderer.setSize(w, h);
      renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
      camera.updateProjectionMatrix();
    };
    window.addEventListener("resize", onResize);

    const ctx = gsap.context(() => {
      const master = gsap.timeline({
        scrollTrigger: {
          trigger: wrapperRef.current,
          // start: "top 29%", // necesito top top en desktop
          start: () => (window.innerWidth < MOBILE_BP ? "top 29%" : "top top"), // 👈
          end: "+=2500%", // length of the scroll
          scrub: true, // tie the timeline to scrollbar position
          pin: true, // keep the section fixed while animating
        },
      });
      function addTyping(label: string, key: keyof typeof stageTexts) {
        const data = { p: 0 }; // ← independiente por stage

        // 0) justo en ese “label” cambiamos el texto y lo reseteamos
        master.add(() => {
          setTypedText(stageTexts[key]);
          setTextProgress(0);
        }, label);

        // 1) tween que va de 0→1 y actualiza el progreso
        master.fromTo(
          data,
          { p: 0 },
          {
            p: 1,
            ease: "none",
            immediateRender: false, // 🔑 evita el “flash”
            onUpdate() {
              setTextProgress(data.p); // usa **su propio** objeto
            },
          },
          label // mismo instante
        );
      }

      masterRef.current = master;
      master?.scrollTrigger?.disable(); // 🔓 ¡ya depende del scroll!

      const helper = { p: 0 }; // objeto observable

      master.fromTo(
        helper, // target
        { p: 0 },
        {
          p: 1, // 0->1 en el tramo que quieras
          // ease: "none",
          onUpdate() {
            setTextProgress(helper.p);
          },
          immediateRender: false,
        },
        "letterText" // etiqueta donde empieza el tipeo
      );

      master
        .addLabel("letterText", ">")
        .addLabel("letterSpiralIn", ">")
        .addLabel("letterSpiralInRotation", ">");
      master
        .to(
          "#stage1",
          {
            scale: 0,
            autoAlpha: 0,
            onComplete: () => {
              setTypedText("");
            },
            onReverseComplete: () => {
              setTypedText(stageTexts.stage1);
              setShowHint({ active: true, rotated: false });
            },
            onStart: () => {
              setShowHint({ active: false, rotated: false });
            },
          },
          ">"
        )
        .addLabel("letterSpiralIn2", ">")
        .addLabel("letterSpiralInRotation2", ">")
        .fromTo(
          "#stage2",
          {
            scale: 200,
            x: "0vw",
            y: "-7vh",
            autoAlpha: 1,
            opacity: 0,
            willChange: "transform",
          },
          {
            scale: 1,
            x: 0,
            y: 0,
            opacity: 1,
          },
          ">"
        );

      addTyping("letterText2", "stage2");

      master
        .addLabel("letterSpiralOut2", ">")
        .addLabel("letterSpiralOutRotation2", ">")

        .to(
          "#stage2",
          {
            scale: 0,
            autoAlpha: 0,
            onComplete: () => {
              setTypedText("");
            },
            onReverseComplete: () => {
              setTypedText(stageTexts.stage2);
            },
          },
          ">"
        )
        .addLabel("letterSpiralIn3", ">")
        .addLabel("letterSpiralInRotation3", ">")

        .fromTo(
          "#stage3Wrapper",
          {
            scale: 5,
            x: "0vw",
            y: "-7vh",
            autoAlpha: 1,
            opacity: 0,
            willChange: "transform",
          },
          {
            scale: 1,
            x: 0,
            y: 0,
            opacity: 1,
          },
          ">"
        );
      addTyping("letterText3", "stage3");

      master
        .addLabel("letterSpiralOut3", ">")
        .addLabel("letterSpiralOutRotation3", ">")

        .addLabel("stage3Video", ">")
        .to(
          "#stage3Wrapper",
          {
            scale: 0,
            autoAlpha: 0,
            onComplete: () => {
              setTypedText("");
            },
            onReverseComplete: () => {
              setTypedText(stageTexts.stage3);
            },
          },
          "stage3Video+=100%"
        )
        .addLabel("letterSpiralIn4", ">")
        .addLabel("letterSpiralInRotation4", ">")

        .fromTo(
          "#stage4",
          {
            scale: 200,
            x: "0vw",
            y: "-7vh",
            autoAlpha: 1,
            opacity: 0,
            willChange: "transform",
          },
          {
            scale: 1,
            x: 0,
            y: 0,
            opacity: 1,
          },
          ">"
        );
      addTyping("letterText4", "stage4");

      master
        .addLabel("letterSpiralOut4", ">")
        .addLabel("letterSpiralOutRotation4", ">")
        .to(
          "#stage4",
          {
            scale: 0,
            autoAlpha: 0,
            onComplete: () => {
              setTypedText("");
            },
            onReverseComplete: () => {
              setTypedText(stageTexts.stage4);
            },
          },
          ">"
        )
        .addLabel("letterSpiralIn5", ">")
        .addLabel("letterSpiralInRotation5", ">")

        .fromTo(
          "#stage5Wrapper",
          {
            scale: 5,
            x: "0vw",
            y: "-7vh",
            autoAlpha: 1,
            opacity: 0,
            willChange: "transform",
          },
          {
            scale: 1,
            x: 0,
            y: 0,
            opacity: 1,
          },
          ">"
        );
      addTyping("letterText5", "stage5");

      master
        .addLabel("letterSpiralOut5", ">")
        .addLabel("letterSpiralOutRotation5", ">")

        .addLabel("stage5Video", ">")
        .to(
          "#stage5Wrapper",
          {
            scale: 0,
            autoAlpha: 0,
            onComplete: () => {
              setTypedText("");
            },
            onReverseComplete: () => {
              setTypedText(stageTexts.stage5);
            },
          },
          "stage5Video+=100%"
        )
        .addLabel("letterSpiralInFinal", ">")
        .addLabel("letterSpiralInRotationFinal", ">")

        .fromTo(
          "#stageFinal",
          {
            scale: 5,
            x: "0vw",
            y: "-7vh",
            autoAlpha: 1,
            opacity: 0,
          },
          {
            scale: 1,
            x: 0,
            y: 0,
            opacity: 1,
          },
          ">"
        );

      addTyping("letterTextFinal", "stageFinal");

      master
        .addLabel("letterSpiralOutFinal", ">")
        .addLabel("letterSpiralOutRotationFinal", ">")

        .addLabel("stageFinalVideo", ">")
        .to(
          "#stageFinal",
          {
            scale: 0,
            autoAlpha: 0,
            onComplete: () => {
              setTypedText("");
              setShowHint({ active: true, rotated: true });
            },
            onReverseComplete: () => {
              setTypedText(stageTexts.stageFinal);
              setShowHint({ active: false, rotated: false });
            },
          },
          "stageFinalVideo+=100%"
        );

      setTimeline(master);
    }, wrapperRef);

    return () => {
      ctx.revert();
      window.removeEventListener("resize", onResize);
      // Howler.unload(); // dispose all sounds
      renderer.dispose();
      controls.dispose();
    };
  }, []);

  useEffect(() => {
    if (!stage1Texture || !paperMesh) return;

    const mat = paperMesh.material as THREE.MeshBasicMaterial;
    mat.map = stage1Texture;
    mat.needsUpdate = true;
  }, [stage1Texture, paperMesh]); // ⬅️  doble dependencia

  return (
    <main className="h-full  relative">
      {loading && (
        <div
          className="fixed inset-0 z-[300] flex flex-col items-center
                  justify-center bg-black/60 text-white pointer-events-none"
        >
          <p className="text-4xl font-bold tracking-widest">{progress}%</p>
          <p className="mt-2 text-lg">Loading…</p>
        </div>
      )}
      {!loading && !started && (
        <div
          className="fixed inset-0 z-[400] flex flex-col items-center
              justify-center bg-black/80 text-white"
        >
          <button
            onClick={handleStart}
            className="px-8 cursor-pointer py-4 text-2xl font-semibold bg-pink-600 rounded-lg
                                  hover:bg-pink-500 transition-colors"
          >
            Turn up the volume and press to start!
          </button>
        </div>
      )}
      {showHint.active && (
        <div
          className="fixed inset-x-0 bottom-16 z-[250] flex flex-col
                  items-center text-white pointer-events-none"
        >
          {/* flecha ↓ SVG muy simple */}
          <svg
            className={cn("w-8 h-8 animate-bounce", showHint.rotated && "rotate-180")}
            fill="none"
            stroke="currentColor"
            strokeWidth="3"
            viewBox="0 0 24 24"
          >
            <path d="M6 9l6 6 6-6" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          <p className="mt-1 text-lg">Scroll!</p>
        </div>
      )}
      <canvas
        ref={canvasRef}
        id="experience-canvas"
        className="fixed h-full w-full pointer-events-none z-[100]"
      />
      <div className="max-w-[920px] z-[5] mx-auto">
        <section
          ref={wrapperRef}
          className="relative h-[50vh] md:top-0 md:h-screen w-screen overflow-hidden bg-black max-w-[920px]"
        >
          {/* ───── Stage 1 ───── Fondo con manos y flores yendo hacia arriba*/}
          <div
            id="stage1"
            className="absolute inset-0 z-20 flex items-center justify-center will-change-transform"
          >
            <img
              src="/media/stage-1.png"
              alt="Stage 1"
              className="object-cover w-full h-full select-none pointer-events-none"
            />
          </div>

          {/* ───── Stage 2 ───── foto de IA de nosotros dos */}
          <div
            id="stage2"
            className="absolute inset-0 z-10 flex items-center justify-center will-change-transform origin-[38%_32%] md:origin-[36%_32%]"
            /* Adjust the origin until the zoom starts exactly on the eye */
            // style={{ transformOrigin: "36% 32%" }}
          >
            <img
              src="/media/stage-2.jpeg"
              alt="Stage 2"
              className="object-cover w-full h-full select-none pointer-events-none"
            />
          </div>

          {/* ───── Stage 3 (vídeo) ───── Gif abrazo blanco y negro chica y chico dibujo */}
          <div
            id="stage3Wrapper"
            className="absolute  inset-0 z-10 flex items-center justify-center will-change-transform backface-hidden"
            style={{ transformOrigin: "50% 50%" }}
          >
            <ScrollSyncedVideoStage
              gsapTimeline={timeline} // ⬅️  timeline maestro guardado en state
              label="stage3Video" // ⬅️  dónde engancharlo
              src={"/media/stage-3.mp4"}
            />
          </div>

          {/* ───── Stage 4 ───── meme chico chica escribiéndose y dejando el móvil para dormir */}
          <div
            id="stage4"
            className="absolute inset-0 z-10 flex items-center justify-center will-change-transform"
            /* Adjust the origin until the zoom starts exactly on the eye */
            style={{ transformOrigin: "34.5% 32%" }}
          >
            <img
              src={"/media/stage-4.jpeg"}
              alt="Stage 2"
              className="object-cover w-full h-full select-none pointer-events-none"
            />
          </div>

          {/* ───── Stage 5 ───── crear gif con su vídeo de primer curso de japonés, zuzume canción y una foto ghibli o de su hermana*/}
          <div
            id="stage5Wrapper"
            className="absolute  inset-0 z-10 flex items-center justify-center will-change-transform"
            style={{ transformOrigin: "50% 50%" }}
          >
            <video
              src="/media/stage-5/output-cropped.mp4"
              autoPlay
              muted
              loop
              playsInline
              className="object-contain w-full h-full"
            />
          </div>

          {/* ───── Stage final (vídeo) ───── gif sésamo Elmo heart*/}
          <div
            id="stageFinal"
            className="absolute  inset-0 z-10 flex items-center justify-center will-change-transform"
            style={{ transformOrigin: "50% 50%" }}
          >
            <ScrollSyncedVideoStage
              gsapTimeline={timeline} // ⬅️  timeline maestro guardado en state
              label="stageFinalVideo" // ⬅️  dónde engancharlo
              src="/media/stage-final.mp4"
            />
          </div>
        </section>
      </div>
    </main>
  );
};

export { ScrollStages };
